import React, { useMemo } from "react";
import { McpDebugTurn } from "../../../Types/Analyse/AnalyseChatData";
import { useAppSelector } from "../../../Redux/Store/Store";
import { getIsMesmaAdmin } from "../../../Redux/Store/Slices/client";
import styles from "./ToolActivityDebug.module.css";

type ToolActivityDebugProps = {
  turns?: McpDebugTurn[] | null;
  /** Kept for callers; tool strip is always card-embedded. */
  embedded?: boolean;
};

const kindLabel = (kind: string) => {
  switch (kind) {
    case "functionCall":
      return "Call";
    case "functionResult":
      return "Result";
    case "reasoning":
      return "Reasoning";
    case "system":
      return "System";
    default:
      return kind || "Tool";
  }
};

const isAdminOnlyKind = (kind: string) =>
  kind === "system" || kind === "functionResult" || kind === "reasoning";

const ToolActivityDebug = ({ turns, embedded = false }: ToolActivityDebugProps) => {
  const isMesmaAdmin = useAppSelector(getIsMesmaAdmin);

  const visibleTurns = useMemo(() => {
    if (!turns || turns.length === 0) {
      return [];
    }
    if (isMesmaAdmin) {
      return turns;
    }
    return turns.filter((turn) => !isAdminOnlyKind(turn.kind));
  }, [turns, isMesmaAdmin]);

  if (visibleTurns.length === 0) {
    return null;
  }

  return (
    <details className={styles.details} data-testid="analyse-tool-activity">
      <summary className={styles.summary}>
        Tool activity
        <span className={styles.badge}>{visibleTurns.length}</span>
      </summary>
      <ul className={styles.list}>
        {visibleTurns.map((turn, index) => {
          const adminOnly = isAdminOnlyKind(turn.kind);
          return (
            <li
              key={`${turn.messageId ?? turn.kind}-${index}`}
              className={`${styles.item}${adminOnly ? ` ${styles.adminOnlyItem}` : ""}`}
            >
              <div className={styles.meta}>
                <span className={styles.kind}>{kindLabel(turn.kind)}</span>
                {adminOnly ? (
                  <span className={styles.adminBadge} title="Visible to Mesma Admin only">
                    Mesma Admin
                  </span>
                ) : null}
                {turn.name ? <span className={styles.name}>{turn.name}</span> : null}
              </div>
              {turn.summary ? (
                <pre className={styles.summaryText}>{turn.summary}</pre>
              ) : null}
            </li>
          );
        })}
      </ul>
    </details>
  );
};

export default ToolActivityDebug;
